import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import {skipToken} from '@reduxjs/toolkit/query'
import classNames from 'classnames'
import {memo, useEffect, useMemo, useRef, useCallback} from 'react'
import {useLocation} from 'react-router-dom'

import {useRunningBuildUpdater} from '../../../../../containers/RunningBuildUpdater'
import {useProjectData} from '../../../../../hooks/useEntity'
import {useThrottle} from '../../../../../hooks/useThrottle'
import {getBuildTestOccurrencesCount, getExtensionEndpoint} from '../../../../../selectors'
import {restApi} from '../../../../../services/rest'
import {stringifyId} from '../../../../../types'
import type {BuildId} from '../../../../../types'
import {emptyArray} from '../../../../../utils/empty'
import {subscribeOnProjectEvents} from '../../../../../utils/subscriber'
import {
  TEST_MUTE_UPDATED,
  TEST_RESPONSIBILITY_CHANGED,
} from '../../../../../utils/subscriptionEvents'
import Pager from '../../../../common/Pager/Pager.container'
import {usePage} from '../../../../common/Pager/Pager.hooks'
import {PagerGroup} from '../../../../common/Pager/Pager.types'
import TestPagerWarning from '../../../../common/Pager/TestPager/TestPagerWarning'
import {
  getTestOccurrencesInvocationsArg,
  useTestOccurencesArg,
} from '../../../../packages/Tests/Tests.rest'
import {
  getFlakyTestDataByBuildHash,
  makeGetTestOccurrencesList,
} from '../../../../packages/Tests/Tests.selectors'
import {getFilterQueryParams, getTestsLocator} from '../BuildTestsTab.utils'
import {getStatusQueryParam} from '../TestsFilter/TestsStatusFilter/TestsStatusFilter.utils'

import TestsList from './TestsList/TestsList'

import styles from './TestsView.css'

import {useAppSelector} from 'src/hooks/react-redux'

type OwnProps = {
  buildId: BuildId
}

const COUNT_TESTS = 100
const COUNT_INVOCATIONS = 21
const THROTTLER_TIMEOUT = 5000

const requestOptions = {
  withNewFailure: true,
  withMetadataCount: true,
  withNextFixed: true,
  withRunOrder: true,
  withMuteInfo: true,
  withInvestigationInfo: true,
}

const testOccurencesOptions = {
  ...requestOptions,
  withUpdatePager: true,
  withInvocationsCounters: true,
}

function TestsView({buildId}: OwnProps) {
  const location = useLocation()

  const filterQueryParams = getFilterQueryParams(location)

  const projectId = useProjectData({buildId}, project => project?.id)
  const projectInternalId = useProjectData({buildId}, project => project?.internalId)
  const buildTestOccurrencesCount = useAppSelector(state =>
    getBuildTestOccurrencesCount(state, buildId),
  )

  const status = getStatusQueryParam(location)
  const locator = getTestsLocator({
    ...filterQueryParams,
    baseLocator: `build:(id:${stringifyId(buildId)})`,
    currentPage: usePage(),
    count: COUNT_TESTS,
    status,
  })
  const invocationsLocator = getTestsLocator({
    ...filterQueryParams,
    count: COUNT_INVOCATIONS,
    orderBy: null, // TODO remove this when the https://youtrack.jetbrains.com/issue/TW-70618 issue is fixed
    status,
  })

  const arg = useTestOccurencesArg(locator, testOccurencesOptions)
  const {
    ids,
    isReady,
    isLoadingNewLocator,
    refetch: refetchIds,
  } = restApi.endpoints.getAllTestOccurrencesNormalized.useQuery(arg, {
    selectFromResult: ({data, currentData}) => ({
      ids: data?.data.result ?? emptyArray,
      isReady: data != null,
      isLoadingNewLocator: data !== currentData,
    }),
  })

  const invocationsArg = getTestOccurrencesInvocationsArg(locator, invocationsLocator, {
    ...requestOptions,
    withBuildInfo: true,
  })
  const {
    isReadyInvocations,
    isLoadingInvocations,
    refetch: refetchInvocations,
  } = restApi.endpoints.getAllTestOccurrencesNormalized.useQuery(invocationsArg, {
    selectFromResult: ({data, isLoading}) => ({
      isReadyInvocations: data != null,
      isLoadingInvocations: isLoading,
    }),
  })

  const refetch = useCallback(() => {
    refetchIds()
    refetchInvocations()
  }, [refetchIds, refetchInvocations])

  const fetchTestOccurrencesWithThrottle = useThrottle(refetch, THROTTLER_TIMEOUT)

  useRunningBuildUpdater(buildId, window.GLOBAL_FETCH_DISABLED === true)
  useEffect(() => {
    if (projectInternalId != null) {
      return subscribeOnProjectEvents(
        projectInternalId,
        [TEST_RESPONSIBILITY_CHANGED, TEST_MUTE_UPDATED],
        fetchTestOccurrencesWithThrottle,
      )
    }

    return undefined
  }, [fetchTestOccurrencesWithThrottle, projectInternalId])

  useEffect(() => {
    fetchTestOccurrencesWithThrottle()
  }, [buildTestOccurrencesCount, fetchTestOccurrencesWithThrottle])

  const lastReadyInvocationsLocator = useRef(locator) // initial value
  if (isReadyInvocations && !isLoadingInvocations) {
    lastReadyInvocationsLocator.current = locator
  }

  const getTestOccurrencesList = useMemo(makeGetTestOccurrencesList, [])
  const testOccurrences = useAppSelector(state => getTestOccurrencesList(state, ids))
  const flakyTests = useAppSelector(state => getFlakyTestDataByBuildHash(state, buildId))
  const flakyTestsEndpoint = useAppSelector(state => getExtensionEndpoint(state, 'flaky'))

  const tests = testOccurrences
    .map(testOccurrence => ({
      testName: testOccurrence.test?.id,
      buildTypeId: testOccurrence.build?.buildType?.internalId,
    }))
    .filter(test => test.testName != null && test.buildTypeId != null && !flakyTests[test.testName])
  restApi.endpoints.getFlakyTests.useQuerySubscription(
    tests.length > 0 && flakyTestsEndpoint != null
      ? {
          buildId,
          tests,
          endpoint: flakyTestsEndpoint.endpoint,
        }
      : skipToken,
  )

  return isReady && lastReadyInvocationsLocator.current != null ? (
    <>
      <TestsList
        className={classNames({
          [styles.overlay]:
            isLoadingNewLocator ||
            (isLoadingInvocations && locator !== lastReadyInvocationsLocator.current),
        })}
        key={locator}
        buildId={buildId}
        projectId={projectId}
        invocationsArg={invocationsArg}
        ids={ids}
        fetchTestOccurrencesHandler={fetchTestOccurrencesWithThrottle}
      />
      <Pager
        group={PagerGroup.TEST}
        warningComponent={TestPagerWarning}
        isLoading={isLoadingNewLocator}
      />
    </>
  ) : (
    <LoaderInline>{'Loading...'}</LoaderInline>
  )
}

export default memo<OwnProps>(TestsView)
